import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useUser } from '../../app/session';
import { toast } from '../../components/toasts';
import { Badge, Button, ErrorText, Spinner } from '../../components/ui';
import { errorMessage, get, hasPermission, put } from '../../lib/api';
import { since } from '../../lib/format';
import { AgentView } from '../../lib/types';

interface TelephonyOverview {
  pbx: { enabled: boolean; connected: boolean; lastConnectedAt: string | null; lastEventAt: string | null; lastError: string | null };
  queue: string | null;
  routingEnabled: boolean;
  directRingSeconds: number;
  softphoneEnabled: boolean;
  agents: AgentView[];
}

/**
 * Central da telefonia na administração: situação do PBX, fila e roteamento, e os operadores da
 * fila em tempo real. Supervisão pausa/libera; administração escolhe quem atende a fila.
 * A fila em si (estratégia de toque, música, tempo máximo) fica no FreePBX.
 */
export function TelephonyAdmin() {
  const user = useUser();
  const qc = useQueryClient();
  const canPause = hasPermission(user, 'attendance:read_all');
  const canManage = hasPermission(user, 'user:update');
  const { data, isLoading, error } = useQuery({ queryKey: ['telephony', 'overview'], queryFn: () => get<TelephonyOverview>('/telephony/overview'), refetchInterval: 15_000 });

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast(ok, 'ok');
      void qc.invalidateQueries({ queryKey: ['telephony'] });
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };

  if (isLoading) return <Spinner />;
  if (!data) return <ErrorText error={error} />;
  const pbx = data.pbx;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Info label="PBX (AMI)" ok={pbx.connected} value={!pbx.enabled ? 'desligado' : pbx.connected ? 'conectado' : 'sem conexão'} hint={pbx.lastError ?? (pbx.lastEventAt ? `último evento há ${since(pbx.lastEventAt)}` : undefined)} />
        <Info label="Fila de ligações" ok={Boolean(data.queue)} value={data.queue ? `fila ${data.queue}` : 'não configurada'} hint="TELEPHONY_QUEUE; a fila é criada no FreePBX sem membros fixos" />
        <Info label="Roteamento" ok={data.routingEnabled} value={data.routingEnabled ? `ligado (${data.directRingSeconds} s)` : 'desligado'} hint="Cliente já atendido no WhatsApp toca primeiro para o mesmo operador" />
        <Info label="Telefone no navegador" ok={data.softphoneEnabled} value={data.softphoneEnabled ? 'ligado' : 'desligado'} hint="SIP_WS_URL e SIP_DOMAIN" />
      </div>

      <div>
        <h2 className="mb-1 text-sm font-semibold">Operadores e fila de ligações</h2>
        <p className="mb-2 text-xs text-fg-soft">
          Entra na fila quem tem ramal, está com a plataforma aberta e marcado como "Atende a fila". O ramal e a senha do telefone do navegador ficam em Usuários e agentes → editar.
        </p>
        <table className="w-full overflow-hidden rounded-lg border border-border bg-panel text-sm">
          <thead className="bg-muted text-left text-xs text-fg-soft">
            <tr>
              <th className="px-3 py-2">Operador</th>
              <th className="px-3 py-2">Ramal</th>
              <th className="px-3 py-2">Situação</th>
              <th className="px-3 py-2">Atende a fila</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {data.agents.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-fg-soft">
                  Nenhum usuário com ramal. Cadastre em Usuários e agentes.
                </td>
              </tr>
            )}
            {data.agents.map((a) => (
              <tr key={a.userId} className="border-t border-border">
                <td className="px-3 py-2">
                  <span className="flex items-center gap-2">
                    <span className={clsx('size-2 rounded-full', a.online ? 'bg-ok' : 'bg-border')} title={a.online ? 'online' : 'offline'} />
                    {a.name}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-xs">{a.extension}</td>
                <td className="px-3 py-2">
                  <AgentState a={a} />
                </td>
                <td className="px-3 py-2">
                  <label className="flex items-center gap-1.5 text-xs">
                    <input
                      type="checkbox"
                      checked={a.answersQueue}
                      disabled={!canManage}
                      onChange={(e) => void act(() => put(`/telephony/agents/${a.userId}/queue`, { answersQueue: e.target.checked }), e.target.checked ? `${a.name} atende a fila` : `${a.name} saiu da fila`)}
                    />
                    {a.answersQueue ? 'sim' : 'não'}
                  </label>
                </td>
                <td className="px-3 py-2 text-right">
                  {canPause &&
                    (a.status === 'paused' ? (
                      <Button size="sm" variant="success" onClick={() => void act(() => put(`/telephony/agents/${a.userId}/status`, { status: 'available' }), `${a.name} liberado`)}>
                        Liberar
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => void act(() => put(`/telephony/agents/${a.userId}/status`, { status: 'paused', reason: 'Pausado pela supervisão' }), `${a.name} em pausa`)}>
                        Pausar
                      </Button>
                    ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-fg-soft">
        No FreePBX fica só a fila em si: número, estratégia de toque, música de espera, tempo máximo e destino de falha (ver docs/telefonia-freepbx.md, seção 5.1).
      </p>
    </div>
  );
}

function Info({ label, value, ok, hint }: { label: string; value: string; ok: boolean; hint?: string }) {
  return (
    <div className="rounded-lg border border-border bg-panel px-4 py-3" title={hint}>
      <div className="text-xs text-fg-soft">{label}</div>
      <div className={clsx('mt-0.5 font-semibold', ok ? 'text-ok' : 'text-fg-soft')}>{value}</div>
      {hint && <div className="mt-0.5 truncate text-[11px] text-fg-soft">{hint}</div>}
    </div>
  );
}

function AgentState({ a }: { a: AgentView }) {
  if (!a.answersQueue) return <Badge>não atende a fila</Badge>;
  if (a.status === 'paused') return <Badge tone="warn">Pausa: {a.reason ?? '—'}{a.since ? ` · ${since(a.since)}` : ''}</Badge>;
  if (!a.online) return <Badge>offline (fora da fila)</Badge>;
  if (a.inQueue === false) return <Badge tone="warn">entrando na fila…</Badge>;
  return <Badge tone="ok">na fila</Badge>;
}
